import { Tag } from "antd";
import dayjs from "dayjs";
import React from "react";
import { IoCalendarOutline, IoDocumentTextOutline, IoPersonOutline } from "react-icons/io5";

import { QUOTE_STATUS_COLORS, QUOTE_STATUS_LABELS } from "../../constants";
import { IDataverseQuote } from "../../types";

interface QuoteDetailsHeaderProps {
  quoteNumber: string;
  quoteDate: string;
  quote: IDataverseQuote;
}

const MetaItem: React.FC<{ icon: React.ReactNode; label: string; value: string }> = ({
  icon,
  label,
  value,
}) => (
  <div className="flex items-center gap-1.5 text-sm text-gray-600">
    <span className="text-gray-400">{icon}</span>
    <span className="font-medium text-gray-500">{label}:</span>
    <span className="text-gray-700">{value}</span>
  </div>
);

export const QuoteDetailsHeader: React.FC<QuoteDetailsHeaderProps> = ({
  quoteNumber,
  quoteDate,
  quote,
}) => {
  const statusCode = quote.statuscode;
  const statusColor = QUOTE_STATUS_COLORS[statusCode] || "default";
  const statusLabel =
    quote["statuscode@OData.Community.Display.V1.FormattedValue"] ||
    QUOTE_STATUS_LABELS[statusCode] ||
    "Unknown";

  const expiresOn = quote.expireson || quote.effectiveto;
  const expiresOnFormatted = expiresOn ? dayjs(expiresOn).format("DD-MM-YYYY") : null;

  const createdBy =
    quote["_createdby_value@OData.Community.Display.V1.FormattedValue"] || null;

  const projectRef = quote.dogma_projectreference || null;
  const projectType =
    quote["dogma_projecttype@OData.Community.Display.V1.FormattedValue"] || null;

  return (
    <div className="bg-white rounded-lg p-4 md:p-6 mb-6">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div className="flex-1">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
            Quote {quoteNumber}
          </h1>

          {/* Metadata row */}
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            <MetaItem
              icon={<IoCalendarOutline />}
              label="Created"
              value={quoteDate}
            />

            {expiresOnFormatted && (
              <MetaItem
                icon={<IoCalendarOutline />}
                label="Valid Until"
                value={expiresOnFormatted}
              />
            )}

            {projectRef && (
              <MetaItem
                icon={<IoDocumentTextOutline />}
                label="Project Ref"
                value={projectRef}
              />
            )}

            {projectType && (
              <MetaItem
                icon={<IoDocumentTextOutline />}
                label="Project Type"
                value={projectType}
              />
            )}

            {createdBy && (
              <MetaItem
                icon={<IoPersonOutline />}
                label="Created By"
                value={createdBy}
              />
            )}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Tag color={statusColor} className="text-sm px-3 py-1">
            {statusLabel}
          </Tag>
        </div>
      </div>
    </div>
  );
};
